import React, { createRef, useState } from "react";
import { GeoJSON, Marker, Popup, TileLayer } from "react-leaflet";
import L, { Icon } from "leaflet";
import { geolocated } from "react-geolocated";
import { Icon as AntIcon, Tooltip as AntTooltip } from "antd";
// import MarkerClusterGroup from "react-leaflet-markercluster";

import "./style.css";
import "../../DeviceMap/style.css";
import "leaflet/dist/leaflet.css";

import MapGroup from "../../../components/Map/MapGroup";
import { BaseMap } from "../../../components/Map";

import { geojsonIndia } from '../../../util/methods';
import GeoJSONLabels from "../../../components/GeoJSONLabels/GeoJSONLabels";
import { CATEGORY_COLORS, CATEGORY_ICONS, CATEGORY_LABELS } from '../../../util/drawingUtils';
import { TILE_LAYERS, getLeafletTileConfig, getTileLayerAttribution } from '../../../util/mapTiles';

const geojson = geojsonIndia

class LocationMap extends React.Component {
    state = {
        defaultLocation: [0, 0],
        currentZoom: 6,
        scrollWheelZoom: false,
        // POI (geo-fence) overlay controls
        hiddenPoiCategories: [],
        poiPanelOpen: false,
        loadedPoiCategories: [],
        // Tile layer (default / satellite) switcher
        tileLayerType: 'current',
        // Full-screen (maximize) toggle — scoped to this map only
        isMaximized: false,
        isDarkMode:
            typeof document !== 'undefined' && document.body
                ? document.body.classList.contains('dark-theme')
                : false
    };

    mapRef = createRef();
    darkModeObserver = null;

    handleZoomChange = () => {
        if (this.mapRef.current && this.mapRef.current.leafletElement) {
            const zoom = this.mapRef.current.leafletElement.getZoom();
            this.setState({ currentZoom: zoom });
        }
    };

    componentDidMount() {
        // Keep isDarkMode in sync with body class changes
        if (typeof document !== 'undefined' && document.body) {
            this.darkModeObserver = new MutationObserver(() => {
                this.setState({ isDarkMode: document.body.classList.contains('dark-theme') });
            });
            this.darkModeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        }
    }

    componentWillUnmount() {
        if (this.darkModeObserver) this.darkModeObserver.disconnect();
    }

    componentDidUpdate(prevProps, prevState) {
        // Recalculate tile coverage after the container resizes (maximize / restore)
        if (prevState.isMaximized !== this.state.isMaximized) {
            setTimeout(() => {
                if (this.mapRef.current && this.mapRef.current.leafletElement) {
                    this.mapRef.current.leafletElement.invalidateSize();
                }
            }, 300);
        }
    }

    togglePoiCategory = (cat) => {
        this.setState((prev) => ({
            hiddenPoiCategories: prev.hiddenPoiCategories.includes(cat)
                ? prev.hiddenPoiCategories.filter((c) => c !== cat)
                : [...prev.hiddenPoiCategories, cat]
        }));
    };

    resolveOrganisationId() {
        const data = this.props.data || [];
        const org = data.map((a) => a && a.organisation).find(Boolean);
        if (!org) return null;
        if (typeof org === 'string') return org;
        return org._id || org.id || null;
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps && nextProps.coords && nextProps.coords.latitude && nextProps.coords.longitude && JSON.stringify(nextProps.coords) !== JSON.stringify(this.props.coords)) {
            // console.log(nextProps.coords);
            this.setState({
                defaultLocation: [nextProps.coords.latitude, nextProps.coords.longitude]
            })
        }
    }

    render() {
        const { data, coords } = this.props;
        const { tileLayerType, isDarkMode, isMaximized, hiddenPoiCategories, poiPanelOpen, loadedPoiCategories } = this.state;
        const tileCfg = getLeafletTileConfig(isDarkMode);
        const resolvedTileUrl = tileLayerType !== 'current'
            ? (TILE_LAYERS[tileLayerType].url || undefined)
            : (tileCfg.url || undefined);
        const resolvedTileSubdomains = tileLayerType !== 'current'
            ? (TILE_LAYERS[tileLayerType].subdomains || undefined)
            : (tileCfg.subdomains || undefined);
        const organisationId = this.resolveOrganisationId();
        const groupedData = data.reduce((result, current) => {
            const category = (current.location && current.location.name) || "";
            if (!result[category]) {
                result[category] = [];
            }
            result[category].push(current);
            return result;
        }, {});

        const mapData = [];
        Object.entries(groupedData).forEach(([key, value]) => {
            if (key && value && value.length) {
                const location = value[0].location;
                const count = value.length;
                if (location && location.latitude && location.longitude) {
                    const position = [location.latitude, location.longitude];
                    mapData.push({
                        position,
                        count,
                        location
                    });
                }
            }
        });

        const markerIcon = new Icon({
            iconUrl:
                "https://cdn4.iconfinder.com/data/icons/twitter-29/512/157_Twitter_Location_Map-512.png",
            iconSize: [35, 35]
        });

        const markers = mapData.map((doc) => ({
            position: doc.position,
            icon: markerIcon,
            popupContent: `<h2>${doc.location.name || ""}</h2><h4>${doc.count}</h4>`,
            tooltipHtml: `<span class="location-count-badge">${doc.count}</span>`,
        }));

        return (
            <div
                className={`device-map-tab-wrap${isMaximized ? ' device-map-fullscreen' : ''}`}
                style={{ position: 'relative', width: '100%', height: '600px' }}
                onClick={() => this.setState({ scrollWheelZoom: true })}
                onMouseLeave={() => this.setState({ scrollWheelZoom: false })}
            >
                {/* ── Maximize / restore button (full screen for this map only) ── */}
                <button
                    className="device-map-maximize-btn"
                    onClick={(e) => { e.stopPropagation(); this.setState({ isMaximized: !isMaximized }); }}
                    title={isMaximized ? 'Restore map' : 'Maximize map'}
                >
                    <AntIcon type={isMaximized ? 'fullscreen-exit' : 'fullscreen'} />
                </button>

                {/* ── Floating POI category toggle panel (only shown when POI data exists) ── */}
                {loadedPoiCategories.length > 0 && (
                    <div className="device-map-poi-toggle">
                        <button
                            className="device-map-poi-toggle-btn"
                            onClick={(e) => { e.stopPropagation(); this.setState({ poiPanelOpen: !poiPanelOpen }); }}
                            title="Toggle POI categories"
                        >
                            📍 POI Layers {poiPanelOpen ? '▲' : '▼'}
                        </button>
                        {poiPanelOpen && (
                            <div className="device-map-poi-panel">
                                {loadedPoiCategories.map((cat) => {
                                    const hidden = hiddenPoiCategories.includes(cat);
                                    return (
                                        <AntTooltip key={cat} title={CATEGORY_LABELS[cat]} placement="left">
                                            <button
                                                className={`device-map-poi-cat-btn${hidden ? ' device-map-poi-cat-btn-off' : ''}`}
                                                onClick={(e) => { e.stopPropagation(); this.togglePoiCategory(cat); }}
                                                style={{ borderLeftColor: hidden ? '#d9d9d9' : CATEGORY_COLORS[cat] }}
                                            >
                                                <span className="device-map-poi-cat-icon">{CATEGORY_ICONS[cat]}</span>
                                                <span className="device-map-poi-cat-label">{CATEGORY_LABELS[cat]}</span>
                                                {hidden
                                                    ? <span className="device-map-poi-cat-eye">👁‍🗨</span>
                                                    : <span className="device-map-poi-cat-eye" style={{ color: '#52c41a' }}>👁</span>
                                                }
                                            </button>
                                        </AntTooltip>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* ── Floating tile layer switcher (default / satellite) ── */}
                <div className="device-map-tile-switcher">
                    {Object.keys(TILE_LAYERS).map((key) => (
                        <button
                            key={key}
                            className={`device-map-tile-btn${tileLayerType === key ? ' device-map-tile-btn-active' : ''}`}
                            onClick={(e) => { e.stopPropagation(); this.setState({ tileLayerType: key }); }}
                            title={TILE_LAYERS[key].label}
                        >
                            {TILE_LAYERS[key].label}
                        </button>
                    ))}
                </div>

                <BaseMap
                    ref={this.mapRef}
                    center={this.state.defaultLocation}
                    zoom={6}
                    scrollWheelZoom={this.state.scrollWheelZoom}
                    onZoomEnd={this.handleZoomChange}
                    style={{ width: '100%', height: '100%' }}
                    showIndiaGeoJson={true}
                    showMaaGeoJson={false}
                    showGeoFenceOverlay={true}
                    geoFenceOverlayOrganisationId={organisationId}
                    geoFenceOverlayInteractive={false}
                    geoFenceOverlayLabelMinZoom={12}
                    geoFenceOverlayHiddenCategories={hiddenPoiCategories}
                    geoFenceOverlayOnCategoriesLoaded={(cats) => this.setState({ loadedPoiCategories: cats })}
                    tileLayerUrl={resolvedTileUrl}
                    tileLayerMaxZoom={TILE_LAYERS[tileLayerType].maxZoom || 22}
                    tileLayerSubdomains={resolvedTileSubdomains}
                    tileLayerAttribution={getTileLayerAttribution(tileLayerType)}
                    geoJsonLabelsZoom={this.state.currentZoom}
                    geoJsonLabelsMinZoom={17}
                >
                    {/* Hybrid: street label overlay on top of satellite */}
                    {TILE_LAYERS[tileLayerType] && TILE_LAYERS[tileLayerType].overlay && (
                        <TileLayer
                            url={TILE_LAYERS[tileLayerType].overlay}
                            subdomains={TILE_LAYERS[tileLayerType].overlaySubdomains || ['a', 'b', 'c']}
                            opacity={TILE_LAYERS[tileLayerType].overlayOpacity || 0.4}
                            maxZoom={22}
                            attribution={TILE_LAYERS[tileLayerType].overlayAttribution}
                        />
                    )}
                    <MapGroup mapRef={this.mapRef} markers={markers} />
                </BaseMap>
            </div>
        );
    }
}

export default geolocated({
    positionOptions: {
        enableHighAccuracy: false
    },
    userDecisionTimeout: 5000
})(LocationMap);
